import React, { useMemo } from "react";
import books from "./jsonData/BookRecommendation.json";

/**
 * BookRecommendation — polished sidebar widget
 *
 * Props:
 *  - limit?: number (default 4)
 *  - showCovers?: boolean (default true)
 *  - seeAllTo?: string route for "See all" link (default "/resources/books")
 *
 * Expected JSON shape per item:
 *  {
 *    id: string|number,
 *    title: string,
 *    author?: string,
 *    link: string,           // external purchase or docs link
 *    cover?: string,         // image URL
 *    rating?: number,        // 0..5 (e.g., 4.5)
 *    reviews?: number,       // optional count
 *    tag?: string,           // e.g., "System Design"
 *    enabled?: boolean       // if false, hides the item
 *  }
 */

const PLACEHOLDER_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='84' viewBox='0 0 64 84'%3E%3Crect width='64' height='84' rx='6' fill='%23f1f3f5'/%3E%3Cpath d='M16 20h24a4 4 0 0 1 4 4v40a4 4 0 0 1-4 4H16' stroke='%239aa0a6' stroke-width='2' fill='none'/%3E%3Cpath d='M16 20v48' stroke='%239aa0a6' stroke-width='2'/%3E%3Cpath d='M20 28h16M20 36h20M20 44h18' stroke='%23c1c7cd' stroke-width='2'/%3E%3C/svg%3E";

function RatingStars({ value = 0 }) {
  const clamped = Math.max(0, Math.min(5, Number(value) || 0));
  const full = Math.floor(clamped);
  const half = clamped - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="text-warning" aria-label={`${clamped} out of 5 stars`}>
      {"★".repeat(full)}{half ? "☆" : ""}{"☆".repeat(empty)}
    </span>
  );
}

export default function BookRecommendation({ limit = 4, showCovers = true, seeAllTo = "/resources/books" }) {
  const list = useMemo(() => {
    return (Array.isArray(books) ? books : [])
      .filter((b) => b && (b.enabled ?? true))
      .slice(0, limit);
  }, [limit]);

  const handleClick = (b) => {
    try {
      if (window.gtag) {
        window.gtag("event", "select_content", {
          content_type: "book",
          item_id: b.id,
          item_name: b.title,
        });
      }
      if (window.dataLayer) {
        window.dataLayer.push({ event: "book_click", bookId: b.id, bookTitle: b.title });
      }
    } catch {}
  };

  if (!list.length) {
    return (
      <div className="text-muted small">No recommendations yet. Check back soon.</div>
    );
  }

  return (
    <div>
      <ul className="list-group list-group-flush small">
        {list.map((b) => (
          <li className="list-group-item py-2 px-0" key={b.id}>
            <div className="d-flex align-items-start">
              {showCovers && (
                <img
                  src={b.cover || PLACEHOLDER_SVG}
                  alt={b.title}
                  width={48}
                  height={64}
                  className="flex-shrink-0 rounded me-2 border"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = PLACEHOLDER_SVG;
                  }}
                />
              )}
              <div className="flex-grow-1">
                <a
                  href={b.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-semibold text-decoration-none"
                  onClick={() => handleClick(b)}
                >
                  {b.title}
                </a>
                {b.author && (
                  <div className="text-muted">{b.author}</div>
                )}
                <div className="d-flex align-items-center gap-2">
                  {typeof b.rating === "number" && (
                    <>
                      <RatingStars value={b.rating} />
                      {b.reviews ? (
                        <span className="text-muted">({b.reviews.toLocaleString()})</span>
                      ) : null}
                    </>
                  )}
                  {b.tag && (
                    <span className="badge bg-light text-muted border ms-1">{b.tag}</span>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 d-flex justify-content-end">
        <a className="link-secondary small text-decoration-none" href={seeAllTo}>See all ➜</a>
      </div>
      {/* Optional disclosure if you use affiliate links */}
      {/* <div className="form-text mt-1">We may earn a commission from qualifying purchases at no additional cost to you.</div> */}
    </div>
  );
}
